import * as XLSX from "xlsx";
import { GraphQLInputObjectType, GraphQLList, GraphQLNonNull } from "graphql";
import { getOperationDefinition } from "./operationUtils";

interface SheetMeta {
  path: string;
  ws: XLSX.WorkSheet;
}

// Match the sanitizeSheetName function from template.ts for sheet name lookup
function sanitizeSheetName(path: string): string {
  const disallowed = /[\\\/\?\*\[\]\:]/g;
  let name = path.replace(disallowed, "_");
  if (name.length > 31) {
    const hash = name
      .split("")
      .reduce((acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) >>> 0, 0)
      .toString(16);
    const baseLen = 31 - hash.length - 1;
    name = name.slice(0, Math.max(8, baseLen)) + "_" + hash;
    if (name.length > 31) name = name.slice(0, 31);
  }
  return name;
}

// Check if a scalar list sheet has actual data (not just headers/sample row)
function scalarListSheetHasData(
  sheets: SheetMeta[],
  path: string,
  scalarListSheetPaths: Set<string>,
): boolean {
  if (!scalarListSheetPaths.has(path)) return false;

  const sheet = sheets.find((s) => s.path === path);
  if (!sheet) return false;

  const data = XLSX.utils.sheet_to_json<any>(sheet.ws, { header: 1, range: 1 });
  // data[0] = headers, data[1] = sample row (ignored), data[2+] = actual data
  // Check if there's any row beyond the sample row with actual data
  for (let i = 2; i < data.length; i++) {
    const row = data[i];
    // Check if this row has any non-empty values (besides the ref column)
    if (row && row.length > 1) {
      const valueIdx = (data[0] as string[]).indexOf("value");
      if (
        valueIdx >= 0 &&
        row[valueIdx] !== undefined &&
        row[valueIdx] !== ""
      ) {
        return true;
      }
    }
  }
  return false;
}

export interface ParseResult {
  rows: any[];
  warnings: string[];
}

export function parseWorkbook(
  wb: XLSX.WorkBook,
  schemaSDL: string,
  operationName: string,
  operationType: "mutation" | "query" = "mutation",
): ParseResult {
  const mutation = getOperationDefinition(
    schemaSDL,
    operationType,
    operationName,
  );
  const listFieldMeta = collectListFieldMeta(
    schemaSDL,
    operationName,
    operationType,
  );
  const listFieldPaths = new Set(listFieldMeta.map((m) => m.path));
  const scalarListPaths = new Set(
    listFieldMeta.filter((m) => !m.itemIsObject).map((m) => m.path),
  );
  const objectFieldMeta = collectObjectFieldMeta(
    schemaSDL,
    operationName,
    operationType,
  );
  const scalarTypeMap = collectScalarFieldTypes(
    schemaSDL,
    operationName,
    operationType,
  );

  const sheets: SheetMeta[] = wb.SheetNames.map((name) => {
    const ws = wb.Sheets[name];
    const pathCell = ws["A1"];
    if (!pathCell) throw new Error(`Sheet ${name} missing path in A1`);
    return { path: String(pathCell.v), ws };
  });

  // Identify scalar list sheets early (needed for variables sheet processing)
  const otherSheets = sheets
    .filter((s) => s.path !== "variables")
    .sort((a, b) => a.path.split(".").length - b.path.split(".").length);

  const scalarListSheets = otherSheets.filter((s) => {
    const sheetData = XLSX.utils.sheet_to_json<any>(s.ws, {
      header: 1,
      range: 1,
    });
    const sheetHeaders = sheetData[0] as string[];
    return (
      sheetHeaders.includes("value") && !sheetHeaders.includes("childRowRef")
    );
  });
  const scalarListSheetPaths = new Set(scalarListSheets.map((s) => s.path));

  // Build row map for root variables from the "variables" sheet
  const warnings: string[] = [];
  const rootRowsMap: Record<string, any> = {};
  const idMap: Record<string, Array<{ obj: any; path: string }>> = {};

  const varSheet = sheets.find((s) => s.path === "variables");
  if (!varSheet) throw new Error("Missing sheet for root variables");
  const data = XLSX.utils.sheet_to_json<any>(varSheet.ws, {
    header: 1,
    range: 1,
  });
  const headers = data[0] as string[];

  // Validate headers against schema
  const expectedPaths = new Set([
    ...Object.keys(scalarTypeMap),
    ...listFieldMeta.map((m) => m.path),
    ...objectFieldMeta.map((m) => m.path),
  ]);
  const unknownHeaders = headers.filter(
    (h) => h !== "rowRef" && !expectedPaths.has(h),
  );
  if (unknownHeaders.length > 0) {
    warnings.push(
      `variablesシート: 以下のカラムは選択中のオペレーション "${operationName}" に存在しません: ${unknownHeaders.join(", ")}`,
    );
  }

  const lastVarRow = findLastDataRow(data, 2);
  if (lastVarRow < 2) {
    warnings.push(
      "variablesシートにデータ行がありません（3行目以降にデータを入力してください）",
    );
  }
  const skippedEmptyRefRows: number[] = [];
  for (let i = 2; i <= lastVarRow; i++) {
    const row = data[i];
    const rowRef = row[0];
    if (!rowRef) {
      skippedEmptyRefRows.push(i + 2);
      continue;
    }
    if (!rootRowsMap[rowRef]) rootRowsMap[rowRef] = {};
    headers.forEach((h, idx) => {
      if (idx === 0) return;
      // Skip if this scalar list has its own sheet with data (will be processed later)
      if (
        scalarListPaths.has(h) &&
        scalarListSheetHasData(sheets, h, scalarListSheetPaths)
      ) {
        return;
      }
      const val = row[idx];
      if (val === "" || val === undefined) return;
      // Handle explicit empty array for object list fields
      if (String(val) === "[]" && listFieldPaths.has(h)) {
        setPathValue(rootRowsMap[rowRef], h, []);
        return;
      }
      setPathValue(
        rootRowsMap[rowRef],
        h,
        parseCellValue(val, scalarTypeMap[h], scalarListPaths.has(h)),
      );
    });
    if (!idMap[rowRef]) idMap[rowRef] = [];
    idMap[rowRef].push({ obj: rootRowsMap[rowRef], path: "" });
  }
  if (skippedEmptyRefRows.length > 0) {
    warnings.push(
      `variablesシート: 行${skippedEmptyRefRows.join(", ")}のrowRefが未入力のためスキップされました`,
    );
  }

  // Validate child sheet paths against schema
  const expectedSheetPaths = new Set([
    ...listFieldMeta.filter((m) => m.itemIsObject).map((m) => m.path),
    ...objectFieldMeta.map((m) => m.path),
    ...listFieldMeta.filter((m) => !m.itemIsObject).map((m) => m.path),
  ]);
  const unknownSheets = otherSheets.filter(
    (s) => !expectedSheetPaths.has(s.path),
  );
  if (unknownSheets.length > 0) {
    warnings.push(
      `以下のシートは選択中のオペレーション "${operationName}" に存在しません: ${unknownSheets.map((s) => s.path).join(", ")}`,
    );
  }

  // Process object sheets (non-scalar-list sheets)
  const objectSheets = otherSheets.filter(
    (s) => !scalarListSheetPaths.has(s.path),
  );
  const skippedOrphans: string[] = [];
  for (const sheet of objectSheets) {
    const data = XLSX.utils.sheet_to_json<any>(sheet.ws, {
      header: 1,
      range: 1,
    });
    const headers = data[0] as string[];
    const parentRefIdx = headers.indexOf("_parentRef");
    const childRefIdx = headers.indexOf("childRowRef");
    const lastChildRow = findLastDataRow(data, 2);
    for (let i = 2; i <= lastChildRow; i++) {
      const row = data[i];
      const parentRef = row[parentRefIdx];
      const childRef = row[childRefIdx];
      if (!parentRef) continue;
      const parentList = idMap[parentRef];
      if (!parentList) {
        skippedOrphans.push(
          `${sheet.path}シート: 行${i + 2}の_parentRef "${parentRef}"が見つかりません`,
        );
        continue;
      }
      const parentInfo = parentList.find((p) => p.path !== sheet.path);
      if (!parentInfo) continue;
      const childObj = buildObjFromScalarRow(
        headers,
        row,
        sheet.path,
        scalarTypeMap,
        scalarListPaths,
      );
      const relPath = sheet.path.startsWith(parentInfo.path)
        ? sheet.path.slice(
            parentInfo.path.length > 0 ? parentInfo.path.length + 1 : 0,
          )
        : sheet.path;
      const isList = listFieldPaths.has(sheet.path);
      attachChild(parentInfo.obj, relPath, childObj, isList);
      if (childRef) {
        if (!idMap[childRef]) idMap[childRef] = [];
        idMap[childRef].push({ obj: childObj, path: sheet.path });
      }
    }
  }
  if (skippedOrphans.length > 0) {
    warnings.push(...skippedOrphans);
  }

  // Process scalar list sheets
  for (const sheet of scalarListSheets) {
    const data = XLSX.utils.sheet_to_json<any>(sheet.ws, {
      header: 1,
      range: 1,
    });
    const headers = data[0] as string[];
    const parentRefIdx = headers.indexOf("_parentRef");
    const rowRefIdx = headers.indexOf("rowRef");
    const valueIdx = headers.indexOf("value");

    // Get the scalar type for this list
    const meta = listFieldMeta.find((m) => m.path === sheet.path);
    const itemType = meta?.itemType;

    // Group values by parent ref
    const valuesByParent: Record<string, any[]> = {};
    const lastScalarRow = findLastDataRow(data, 2);

    for (let i = 2; i <= lastScalarRow; i++) {
      const row = data[i];
      const parentRef = parentRefIdx >= 0 ? row[parentRefIdx] : row[rowRefIdx];
      const value = row[valueIdx];

      if (!parentRef) continue;
      if (!valuesByParent[parentRef]) valuesByParent[parentRef] = [];

      // Handle NULL magic value
      if (value === "NULL") {
        valuesByParent[parentRef].push(null);
      } else if (value !== "" && value !== undefined) {
        const parsedValue = parseCellValue(value, itemType, false);
        valuesByParent[parentRef].push(parsedValue);
      }
    }

    // Attach to parent objects
    for (const [parentRef, values] of Object.entries(valuesByParent)) {
      const parentList = idMap[parentRef];
      if (!parentList) continue;

      const parentInfo = parentList.find((p) => p.path !== sheet.path);
      if (!parentInfo) continue;

      const relPath = sheet.path.startsWith(parentInfo.path)
        ? sheet.path.slice(
            parentInfo.path.length > 0 ? parentInfo.path.length + 1 : 0,
          )
        : sheet.path;

      setPathValue(parentInfo.obj, relPath, values);
    }
  }

  // After attaching, ensure array and object fields have defaults
  Object.values(rootRowsMap).forEach((rowObj) => {
    listFieldMeta.forEach(({ path, nonNull }) => {
      const val = getPathValue(rowObj, path);
      // Only set defaults for undefined, not null (null is explicit)
      if (val === undefined) {
        // Don't create intermediate objects if a parent was explicitly set to null or is an array
        const parts = path.split(".");
        let blocked = false;
        let cur: any = rowObj;
        for (let i = 0; i < parts.length - 1; i++) {
          const next = cur[parts[i]];
          if (next === null || Array.isArray(next)) {
            blocked = true;
            break;
          }
          if (next === undefined || typeof next !== "object") {
            break;
          }
          cur = next;
        }
        if (!blocked) {
          setPathValue(rowObj, path, nonNull ? [] : null);
        }
      } else if (val === null) {
        // Keep explicit null as is
      } else if (Array.isArray(val) && val.length === 0) {
        // Keep explicit empty array as is (user typed [] in cell)
      }
    });
    applyObjectDefaults(rowObj, objectFieldMeta);
  });

  const rows = Object.values(rootRowsMap);
  return { rows, warnings };
}

function buildObjFromScalarRow(
  headers: string[],
  row: any[],
  sheetPath: string,
  scalarTypeMap: Record<string, string>,
  scalarListPaths: Set<string>,
): Record<string, any> {
  const obj: Record<string, any> = {};
  headers.forEach((key, idx) => {
    if (["rowRef", "_parentRef", "childRowRef"].includes(key)) return;
    const val = row[idx];

    // Handle NULL magic value explicitly
    if (val === "NULL") {
      obj[key] = null;
      return;
    }

    // Skip empty values (they become undefined/omitted)
    if (val === "" || val === undefined) return;

    // Handle explicit empty array
    if (String(val) === "[]") {
      obj[key] = [];
      return;
    }

    obj[key] = parseCellValue(
      val,
      scalarTypeMap[`${sheetPath}.${key}`],
      scalarListPaths.has(`${sheetPath}.${key}`),
    );
  });
  return obj;
}

function parseCellValue(val: any, typeHint?: string, isList = false): any {
  // Handle NULL magic value first
  if (val === "NULL") return null;

  // Return undefined for empty values (but this shouldn't happen due to buildObjFromScalarRow)
  if (val === null || val === undefined || val === "") return undefined;

  if (isList) {
    if (Array.isArray(val)) {
      return val.map((v) => parseCellValue(v, typeHint, false));
    }
    if (typeof val === "string") {
      // Special case: if the entire field is "NULL", return null for the whole list
      if (val === "NULL") return null;

      return val
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "")
        .map((v) => (v === "NULL" ? null : parseCellValue(v, typeHint, false)));
    }
    return [parseCellValue(val, typeHint, false)];
  }

  switch (typeHint) {
    case "Boolean": {
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val === 1;
      if (typeof val === "string") {
        const lower = val.toLowerCase();
        if (lower === "true") return true;
        if (lower === "false") return false;
      }
      return Boolean(val);
    }
    case "Int":
    case "Float":
      return Number(val);
    case "ID":
    case "JSON":
    case "String":
    default:
      if (typeHint === "JSON") {
        if (typeof val === "string") {
          try {
            return JSON.parse(val);
          } catch {
            throw new Error("Invalid JSON");
          }
        }
        if (typeof val === "object") return val;
        try {
          return JSON.parse(String(val));
        } catch {
          throw new Error("Invalid JSON");
        }
      }
      return String(val);
  }
}

function attachChild(
  parentObj: any,
  relPath: string,
  childObj: any,
  isList: boolean,
): void {
  const parts = relPath.split(".");
  let obj = parentObj;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isLast = i === parts.length - 1;
    if (isLast) {
      if (isList) {
        if (!obj[part]) obj[part] = [];
        obj[part].push(childObj);
      } else {
        obj[part] = childObj;
      }
    } else {
      if (!obj[part]) obj[part] = {};
      obj = obj[part];
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers for empty-array handling
// ---------------------------------------------------------------------------
interface ListFieldMeta {
  path: string; // e.g., input.items
  nonNull: boolean; // true => must be [] if empty
  itemIsObject: boolean;
  itemType?: string;
}

interface ObjectFieldMeta {
  path: string; // e.g., input.info
  nonNull: boolean;
}

function collectListFieldMeta(
  schemaSDL: string,
  name: string,
  type: "mutation" | "query" = "mutation",
): ListFieldMeta[] {
  const mutation = getOperationDefinition(schemaSDL, type, name);
  const queue: Array<{ path: string; type: any }> = [];
  mutation.args.forEach((arg) =>
    queue.push({ path: arg.name, type: arg.type }),
  );

  const listMeta: ListFieldMeta[] = [];

  while (queue.length) {
    const { path, type } = queue.shift()!;
    let t: any = type;
    let nonNull = false;
    if (t instanceof GraphQLNonNull) {
      nonNull = true;
      t = t.ofType;
    }
    if (t instanceof GraphQLList) {
      const inner =
        t.ofType instanceof GraphQLNonNull ? t.ofType.ofType : t.ofType;
      listMeta.push({
        path,
        nonNull,
        itemIsObject: inner instanceof GraphQLInputObjectType,
        itemType:
          inner instanceof GraphQLInputObjectType ? undefined : inner.name,
      });
      t = inner;
    }
    if (t instanceof GraphQLInputObjectType) {
      const fields = t.getFields();
      for (const fname of Object.keys(fields)) {
        queue.push({ path: `${path}.${fname}`, type: fields[fname].type });
      }
    }
  }

  return listMeta;
}

function collectObjectFieldMeta(
  schemaSDL: string,
  name: string,
  type: "mutation" | "query" = "mutation",
): ObjectFieldMeta[] {
  const mutation = getOperationDefinition(schemaSDL, type, name);
  const queue: Array<{ path: string; type: any }> = [];
  mutation.args.forEach((arg) =>
    queue.push({ path: arg.name, type: arg.type }),
  );

  const objMeta: ObjectFieldMeta[] = [];

  while (queue.length) {
    const { path, type } = queue.shift()!;
    let t: any = type;
    let nonNull = false;
    if (t instanceof GraphQLNonNull) {
      nonNull = true;
      t = t.ofType;
    }
    let isList = false;
    if (t instanceof GraphQLList) {
      isList = true;
      t = t.ofType instanceof GraphQLNonNull ? t.ofType.ofType : t.ofType;
    }
    if (t instanceof GraphQLInputObjectType) {
      if (!isList) objMeta.push({ path, nonNull });
      const fields = t.getFields();
      for (const fname of Object.keys(fields)) {
        queue.push({ path: `${path}.${fname}`, type: fields[fname].type });
      }
    }
  }

  return objMeta;
}

function collectScalarFieldTypes(
  schemaSDL: string,
  name: string,
  type: "mutation" | "query" = "mutation",
): Record<string, string> {
  const mutation = getOperationDefinition(schemaSDL, type, name);
  const queue: Array<{ path: string; type: any }> = [];
  mutation.args.forEach((arg) =>
    queue.push({ path: arg.name, type: arg.type }),
  );

  const map: Record<string, string> = {};

  while (queue.length) {
    const { path, type } = queue.shift()!;
    let t: any = type;
    if (t instanceof GraphQLNonNull) t = t.ofType;
    if (t instanceof GraphQLList)
      t = t.ofType instanceof GraphQLNonNull ? t.ofType.ofType : t.ofType;
    if (t instanceof GraphQLInputObjectType) {
      const fields = t.getFields();
      for (const fname of Object.keys(fields)) {
        queue.push({ path: `${path}.${fname}`, type: fields[fname].type });
      }
    } else {
      map[path] = t.name;
    }
  }

  return map;
}

function getPathValue(obj: any, path: string): any {
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function setPathValue(obj: any, path: string, value: any): void {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

function applyObjectDefaults(obj: any, meta: ObjectFieldMeta[]): void {
  meta.forEach(({ path, nonNull }) => {
    const parts = path.split(".");
    applyRec(obj, parts, nonNull);
  });
}

function applyRec(cur: any, parts: string[], nonNull: boolean): void {
  if (cur == null) return;
  const [head, ...rest] = parts;
  if (rest.length === 0) {
    if (Array.isArray(cur)) {
      cur.forEach((item) => handleLeaf(item, head, nonNull));
    } else {
      handleLeaf(cur, head, nonNull);
    }
    return;
  }
  if (Array.isArray(cur[head])) {
    cur[head].forEach((item: any) => applyRec(item, rest, nonNull));
  } else {
    if (cur[head] === undefined) cur[head] = {};
    applyRec(cur[head], rest, nonNull);
  }
}

/** Find last row index (in data array) that has any non-empty cell.
 *  Scans forward from startIdx and stops at the first completely empty row,
 *  so stray data at the far end of the sheet (e.g. Excel ghost cells) is ignored.
 */
export function findLastDataRow(data: any[][], startIdx: number): number {
  for (let i = startIdx; i < data.length; i++) {
    const row = data[i];
    if (
      !row ||
      row.every(
        (cell: any) => cell === undefined || cell === null || cell === "",
      )
    ) {
      return i - 1;
    }
  }
  return data.length - 1;
}

function handleLeaf(obj: any, key: string, nonNull: boolean): void {
  let val = obj[key];
  // Only set defaults for undefined, not null (null is explicit)
  if (val === undefined) {
    obj[key] = nonNull ? {} : null;
    return;
  }
  if (val === null) {
    // Keep explicit null as is
    return;
  }
  if (Array.isArray(val)) {
    obj[key] = val.length > 0 ? val[0] : nonNull ? {} : null;
  } else if (val && typeof val === "object") {
    if (Object.values(val).every((v) => v == null) && !nonNull) {
      obj[key] = null;
    }
  }
}
